import { useState, useEffect, useRef } from 'react';
import Spinner from './Spinner';
import { MdOutlineAdd, MdClose } from "react-icons/md";
import { BsPuzzle } from "react-icons/bs";



const GlobalConnectionItem = ({ globalConn, onInitiateOauth, onSaveCustom }) => {
    // State for the form that appears for custom headers
    const [isExpanded, setIsExpanded] = useState(false);
    // State to hold user input for each required header
    const [headerValues, setHeaderValues] = useState({});
    // State to show a loading spinner on the button
    const [isConnecting, setIsConnecting] = useState(false);

    // Check if the connection is custom and has headers that the user needs to fill in
    const hasRequiredHeaders = globalConn.auth_type === 'custom_header' && globalConn.required_headers?.length > 0;

    // Update state as the user types in the header fields
    const handleInputChange = (headerName, value) => {
        setHeaderValues(prev => ({ ...prev, [headerName]: value }));
    };

    const handleConnect = async () => {
        // 1. If we need user input, just expand the form and wait.
        if (hasRequiredHeaders && !isExpanded) {
            setIsExpanded(true);
            return;
        }

        // 2. If we are ready to connect, show the spinner.
        setIsConnecting(true);
        try {
            if (globalConn.auth_type === 'oauth2') {
                // For OAuth, connect immediately.
                await onInitiateOauth({
                    connection_name: globalConn.origin_name, // Use the default name
                    sse_url: globalConn.sse_url,
                    origin: globalConn.origin,
                });
            } else if (globalConn.auth_type === 'custom_header') {
                // For custom headers, build the headers object from user input.
                // If there were no required headers, this will be an empty object.
                const headers = headerValues;
                
                // Optional: A simple check to ensure all fields are filled
                if (hasRequiredHeaders) {
                    for (const key of globalConn.required_headers) {
                        if (!headers[key]) {
                            alert(`Please fill in the '${key}' field.`);
                            setIsConnecting(false);
                            return;
                        }
                    }
                }

                await onSaveCustom({
                    connection_name: globalConn.origin_name, // Use the default name
                    sse_url: globalConn.sse_url,
                    headers: headers,
                });
                setIsExpanded(false); // Close the form on success
            }
        } catch (error) {
            console.error("Connection failed:", error);
            // Parent component's handler will show the alert
        } finally {
            // Always stop the spinner
            setIsConnecting(false);
        }
    };

    // Determine the button text based on the component's state
    let buttonText = '+ Connect';
    if (hasRequiredHeaders) {
        buttonText = isExpanded ? 'Save Connection' : 'Configure & Connect';
    }


    const modalRef = useRef();

    // Close modal on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        if (isExpanded) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isExpanded]);

    return (
        <div
            key={`global-conn-${globalConn.id}`}
            className="flex w-fit max-w-xl flex-wrap items-center border border-zinc-400 rounded-full gap-2 p-1 px-2  bg-white dark:bg-zinc-800 transition-all duration-300"
            title={globalConn.description}
        >
            {/* Logo */}

            <div className="bg-zinc-200 rounded-full p-1 cursor-pointer">
                {globalConn.logo_url ? <img
                    src={globalConn.logo_url}
                    alt={`${globalConn.origin_name} logo`}
                    className="h-5 w-5 object-contain"
                />
                    : <BsPuzzle className="w-4 h-4 text-zinc-700" />}
            </div>

            {/* App Name & Description */}
            <div className="flex flex-col justify-center ml-1 text-zinc-800 dark:text-gray-200 text-sm">
                <span className="font-semibold truncate max-w-[140px]">
                    {globalConn.origin_name}
                </span>
                {globalConn.description && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[160px]">
                        {globalConn.description}
                    </span>
                )}
            </div>

            <button
                onClick={handleConnect}
                disabled={isConnecting}
                className="bg-zinc-100 hover:bg-zinc-300 transition-all duration-300 p-2 rounded-full text-xs text-zinc-700 font-bold ml-4"
            >
                {isConnecting ? <Spinner size="1rem" /> : <MdOutlineAdd size={10} />}
            </button>



            {/* Modal */}
            {hasRequiredHeaders && isExpanded && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div
                        ref={modalRef}
                        className="bg-zinc-800 w-full max-w-lg p-6 rounded-lg shadow-lg border border-zinc-700 space-y-4 relative"
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setIsExpanded(false)}
                            className="absolute top-3 right-3 text-white hover:text-red-400"
                        >
                            <MdClose size={20} />
                        </button>

                        {/* Modal Header */}
                        <h2 className="text-lg font-semibold text-white">
                            Connect to {globalConn.origin_name}
                        </h2>

                        <p className="text-sm text-gray-300">
                            Please provide the following headers to connect:
                        </p>

                        {/* Header Inputs */}
                        {globalConn.required_headers.map((headerName) => (
                            <div key={headerName}>
                                <label
                                    htmlFor={`header-${headerName}`}
                                    className="text-sm font-medium text-gray-300"
                                >
                                    {headerName}
                                </label>
                                <input
                                    id={`header-${headerName}`}
                                    type="text"
                                    value={headerValues[headerName] || ""}
                                    onChange={(e) =>
                                        handleInputChange(headerName, e.target.value)
                                    }
                                    className="mt-1 w-full bg-zinc-900 border border-zinc-600 rounded-md px-3 py-2 text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#5652CF] focus:outline-none"
                                    placeholder={`Enter your ${headerName}`}
                                />
                            </div>
                        ))}

                        {/* Modal Actions */}
                        <div className="flex justify-end gap-2 pt-4">
                            <button
                                onClick={handleConnect}
                                disabled={isConnecting}
                                className="px-4 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                            >
                                {isConnecting && <Spinner size="1.2rem" />}
                                Save Connection
                            </button>
                            <button
                                onClick={() => setIsExpanded(false)}
                                className="px-4 py-2 bg-zinc-600 text-white font-semibold rounded-md hover:bg-zinc-700"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalConnectionItem;